import React, { useMemo } from 'react';

interface VirtualKeyboardProps {
  onKeyPress: (key: string) => void;
  disabled?: boolean;
  currentWord?: string;
}

const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({
  onKeyPress,
  disabled,
  currentWord = '',
}) => {
  // Generate a set of characters to display (word characters + random ones)
  const displayedKeys = useMemo(() => {
    // Get unique characters from the current word (lowercase), excluding spaces
    const wordChars = Array.from(new Set(currentWord.toLowerCase().split(''))).filter(char => char !== ' ');
    
    // All possible characters
    const allLetters = 'abcdefghijklmnopqrstuvwxyz'.split('');
    
    // Filter out characters already in the word
    const remainingLetters = allLetters.filter(letter => !wordChars.includes(letter));
    
    // Shuffle the remaining letters
    const shuffledRemaining = [...remainingLetters].sort(() => Math.random() - 0.5);
    
    // Calculate how many random letters to add (to reach max 15 total keys including backspace)
    const maxTotalKeys = 15;
    const randomLettersCount = Math.max(0, maxTotalKeys - wordChars.length - 1); // -1 for backspace
    
    // Combine word characters and random characters
    const combinedKeys = [
      ...wordChars,
      ...shuffledRemaining.slice(0, randomLettersCount)
    ];
    
    // Shuffle the combined keys for a random arrangement
    const shuffledKeys = [...combinedKeys].sort(() => Math.random() - 0.5);
    
    return shuffledKeys;
  }, [currentWord]);
  
  // Create a circular layout pattern
  const getPositionStyle = (index: number, totalKeys: number) => {
    // For a more interesting layout, we'll create a slight arc pattern
    // with some randomness
    
    // Random offset for playfulness
    const randomX = Math.floor(Math.random() * 20) - 10; // -10 to 10px
    const randomY = Math.floor(Math.random() * 16) - 8;  // -8 to 8px
    
    // Slight rotation for the keys
    const randomRotation = Math.floor(Math.random() * 6) - 3; // -3 to 3 degrees
    
    return {
      transform: `translate(${randomX}px, ${randomY}px) rotate(${randomRotation}deg)`,
      animation: `fadeIn 0.4s ease-out forwards`,
      animationDelay: `${index * 0.06}s`
    };
  };
  
  // Generate colorful gradients for keys
  const getKeyColor = (key: string, isWordLetter: boolean) => {
    // Define a set of vibrant color combinations for gradients
    const colorPairs = [
      { from: 'from-pink-400', to: 'to-purple-500', border: 'border-purple-300' },
      { from: 'from-blue-400', to: 'to-indigo-500', border: 'border-indigo-300' },
      { from: 'from-green-400', to: 'to-teal-500', border: 'border-teal-300' },
      { from: 'from-yellow-300', to: 'to-amber-500', border: 'border-amber-300' },
      { from: 'from-orange-400', to: 'to-red-500', border: 'border-red-300' },
      { from: 'from-purple-400', to: 'to-fuchsia-500', border: 'border-fuchsia-300' },
      { from: 'from-cyan-400', to: 'to-blue-500', border: 'border-blue-300' },
      { from: 'from-lime-400', to: 'to-green-500', border: 'border-green-300' },
    ];
    
    // For word letters, use a consistent color scheme to help children identify them
    if (isWordLetter) {
      return {
        gradient: `bg-gradient-to-br from-indigo-400 to-purple-600`,
        border: `border-2 border-indigo-300`,
        text: `text-white`
      };
    }
    
    // For non-word letters, use a variety of colors based on the character code
    // This ensures the same letter always gets the same color
    const charCode = key.charCodeAt(0);
    const colorIndex = charCode % colorPairs.length;
    const colorPair = colorPairs[colorIndex];
    
    return {
      gradient: `bg-gradient-to-br ${colorPair.from} ${colorPair.to}`,
      border: `border-2 ${colorPair.border}`,
      text: `text-white`
    };
  };
  
  return (
    <div className="w-full">
      <h3 className="text-center text-sm sm:text-base font-medium text-indigo-700 mb-4 sm:mb-5 flex items-center justify-center">
        <span className="text-xl mr-2 animate-pulse">⌨️</span> 
        <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent font-bold">
          Choose letters to spell the word
        </span>
      </h3>
      
      {/* Creative layout with fancy arrangement */}
      <div className="relative flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-5 max-w-3xl mx-auto px-4 py-6">
        {/* Letter keys */}
        {displayedKeys.map((key, index) => {
          const isWordLetter = currentWord.toLowerCase().includes(key);
          const keyColors = getKeyColor(key, isWordLetter);
          
          return (
            <button
              key={key}
              style={getPositionStyle(index, displayedKeys.length)}
              className={`
                relative aspect-square w-12 sm:w-14 md:w-16
                rounded-lg font-bold text-base sm:text-lg md:text-xl
                shadow-md hover:shadow-lg transform hover:scale-110 
                transition-all duration-200 opacity-0
                ${keyColors.gradient} ${keyColors.border} ${keyColors.text}
                ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
                before:absolute before:inset-0 before:rounded-lg before:bg-white before:bg-opacity-20 before:opacity-0 hover:before:opacity-100 before:transition-opacity
              `}
              onClick={() => onKeyPress(key)}
              disabled={disabled}
            >
              <span className="uppercase">{key}</span>
              
              {/* Add a subtle shine effect */}
              <span className="absolute inset-0 rounded-lg overflow-hidden">
                <span className="absolute top-0 left-[-100%] h-full w-[60%] bg-gradient-to-r from-transparent via-white/20 to-transparent transform rotate-45 animate-shine"></span>
              </span>
            </button>
          );
        })}
        
        {/* Delete button - positioned at the bottom */}
        <div className="w-full flex justify-center mt-4">
          <button
            style={{
              animation: `fadeIn 0.4s ease-out forwards`,
              animationDelay: `${displayedKeys.length * 0.06}s`
            }}
            className={`
              relative h-12 sm:h-14 md:h-16 px-4 sm:px-6 md:px-8
              rounded-full font-bold text-base sm:text-lg
              shadow-md hover:shadow-lg transform hover:scale-105 
              transition-all duration-200 opacity-0
              bg-gradient-to-br from-red-400 to-pink-600 text-white
              flex items-center justify-center space-x-2
              border-2 border-red-300
              ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:from-red-500 hover:to-pink-700'}
              before:absolute before:inset-0 before:rounded-full before:bg-white before:bg-opacity-20 before:opacity-0 hover:before:opacity-100 before:transition-opacity
            `}
            onClick={() => onKeyPress('backspace')}
            disabled={disabled}
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"></path>
            </svg>
            <span>Delete</span>
            
            {/* Add a subtle shine effect */}
            <span className="absolute inset-0 rounded-full overflow-hidden">
              <span className="absolute top-0 left-[-100%] h-full w-[60%] bg-gradient-to-r from-transparent via-white/20 to-transparent transform rotate-45 animate-shine"></span>
            </span>
          </button>
        </div>
      </div>
      
      {/* Add animation keyframes to the document head */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px) scale(0.9); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        
        @keyframes shine {
          0% { left: -100%; }
          100% { left: 200%; }
        }
      `}} />
    </div>
  );
};

export default VirtualKeyboard;
